// @flow

import React from 'react';
import {renderers} from '@stripe-internal/markdoc';
import Box from 'sail/Box';
import {ContentSkeleton, OnlyRenderOnClient, Callout} from 'docs/components';
import * as components from 'docs/components';

import {
  FileProvider,
  FileDownloadProvider,
  FileRefProvider,
  StepProvider,
  ConditionProvider,
  DataProvider,
} from 'docs/data/integration-builder';
import type {Article} from 'docs/data';

import {defineMessages, FormattedMessage} from 'react-intl';
import classnames from 'classnames';

const LoadingSkeleton = () => (
  <Box
    flex={{direction: 'column'}}
    flexGrow={1}
    style={{paddingLeft: '5vw', paddingRight: '5vw'}}
  >
    <Box
      background="surface"
      style={{
        height: '40px',
        width: '100%',
        marginTop: '32px',
        marginBottom: '32px',
        borderRadius: '4px',
      }}
    />
    <Box flex={{direction: 'row'}} flexGrow={1} style={{width: '100%'}}>
      <ContentSkeleton />
      <Box
        background="surface"
        flexGrow={1}
        style={{
          width: '70%',
          marginLeft: '4vw',
          marginTop: '8px',
          borderRadius: '8px',
        }}
      />
    </Box>
  </Box>
);

export type MarkdocContentProps = {|
  article: Article,
|};

const messages = defineMessages({
  untranslatedNotice: {
    id: 'docsShell.untranslatedNotice',
    description:
      'A notice to show to indicate that content is not yet translated',
    defaultMessage:
      "This page is not yet available in this language. We're working hard to make our documentation available in more languages and will provide a translation as soon as it's available.",
  },
});

export const IntegrationBuilderContent = ({article}: MarkdocContentProps) => {
  const untranslatedNotice =
    !article.intl.translated && !article.intl.locale.startsWith('en') ? (
      <Callout type="note">
        <p>
          <FormattedMessage {...messages.untranslatedNotice} />
        </p>
      </Callout>
    ) : null;

  const className = classnames(
    'Content-article',
    'MarkdocContentWrapper',
    'Content-IntegrationBuilder',
  );

  return (
    <OnlyRenderOnClient placeholder={<LoadingSkeleton />}>
      <FileProvider>
        <FileDownloadProvider>
          <StepProvider>
            <ConditionProvider>
              <FileRefProvider>
                <DataProvider>
                  <Box className={className}>
                    {untranslatedNotice}
                    <article id="content">
                      {renderers.react(article.content, React, {components})}
                    </article>
                  </Box>
                </DataProvider>
              </FileRefProvider>
            </ConditionProvider>
          </StepProvider>
        </FileDownloadProvider>
      </FileProvider>
    </OnlyRenderOnClient>
  );
};
